import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from '../../i18n/I18nProvider';
import { Button, ProgressBar } from '../../ui';

export interface WizardScreenProps {
  eyebrow: string;
  question: string;
  help?: string;
  /** Redni broj ekrana (od 0) i ukupan broj ekrana — traka napretka ide preko cijelog čarobnjaka. */
  index: number;
  total: number;
  children: ReactNode;
  onNext: () => void;
  onBack?: (() => void) | undefined;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
  /** Poruka greške koja se odnosi na cijeli ekran, ne na jedno polje. */
  error?: string | undefined;
  footer?: ReactNode;
}

/**
 * Jedan ekran čarobnjaka: jedno pitanje, jedan odgovor, Dalje/Natrag.
 *
 * Sve je u `<form>` pa Enter nastavlja bez obzira gdje je fokus — trener je
 * izričito tražio da se „izbacuje jedan po jedan prozorčić”, a ne dugačka forma.
 */
export function WizardScreen({
  eyebrow,
  question,
  help,
  index,
  total,
  children,
  onNext,
  onBack,
  nextLabel,
  nextDisabled = false,
  loading = false,
  error,
  footer,
}: WizardScreenProps) {
  const { t } = useTranslation();

  /*
    Čarobnjak mijenja sadržaj u mjestu — bez pomicanja fokusa novo pitanje se ne
    najavi, a fokus ostane na gumbu „Dalje" koji je u međuvremenu dobio drugu
    ulogu. Fokus zato ide na naslov svakog novog ekrana.
  */
  const questionRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    questionRef.current?.focus();
  }, [index]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (nextDisabled || loading) return;
    onNext();
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-line-subtle bg-surface p-7 shadow-panel"
      aria-labelledby="wizard-question"
    >
      <p className="eyebrow mb-2">{eyebrow}</p>
      <ProgressBar
        className="mb-6"
        value={index + 1}
        max={total}
        label={t('wizard.stepOf', { n: index + 1, total })}
        valueLabel={`${index + 1}/${total}`}
      />

      <h1
        id="wizard-question"
        ref={questionRef}
        tabIndex={-1}
        className="font-display text-[30px] font-semibold leading-tight text-ink outline-none"
      >
        {question}
      </h1>
      {help && <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">{help}</p>}

      <div className="mt-6">{children}</div>

      {error && (
        <p role="alert" className="mt-4 text-[13px] text-danger">
          {error}
        </p>
      )}

      {footer}

      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="ghost" disabled={!onBack} onClick={() => onBack?.()}>
          {t('common.back')}
        </Button>
        <div className="flex items-center gap-4">
          <span className="hidden text-[12px] text-ink-muted sm:inline">{t('wizard.enterHint')}</span>
          <Button type="submit" disabled={nextDisabled} loading={loading}>
            {nextLabel ?? t('common.next')}
          </Button>
        </div>
      </div>
    </form>
  );
}
